/**
 * gpo-required-fix.js
 * 商品ページ / Globo Product Options（GPO）の「必須項目」表示まわりの改善。
 *
 * 前提:
 *   カート追加がブロックされたことの検知とスピナー制御は product-page-fix.js の
 *   initAddToCartFeedback が持つ。このファイルはそこから飛んでくる2つのイベントに
 *   反応するだけで、クリック監視もスピナー操作も一切しない。
 *
 *     'a97:cart-add-probe'   … 「本当にブロックされたか」の問い合わせ。
 *                              未入力があれば e.detail.blocked = true を返す。
 *     'a97:cart-add-blocked' … ブロック確定。ここでエラー表示とサマリーを出す。
 *
 * 対応する問題:
 *   ① 未入力の必須項目が分かりにくい
 *      GPO は該当要素に .has-error と <p class="help-block error">必須項目</p> を出すだけ。
 *      オプション欄は900px近くあり、カート追加ボタンは一番下なので気付けない。
 *      → GPO の直上に「未選択の項目が N 件あります」のサマリーを出し、そこへスクロールする。
 *        項目名クリックで該当フィールドへジャンプ＋フォーカス。
 *        入力が埋まると GPO が .has-error を自分で外すので、それを監視して自動更新する。
 *
 *   ② 予約商品（Globo Pre-Order）で必須項目エラーが一切出ない
 *      GPO は純正のカート追加ボタンを複製して
 *        .gpo-clone-atc-button（表示・クリックを横取りしてバリデーション）
 *        .gpo-original-atc-button（非表示・検証通過時にこちらを押して submit）
 *      の2つにする。Globo Pre-Order はその両方に .gPreorderHidden を付けて display:none にし、
 *      自前の「予約注文」ボタン（.gPreorderBtn）を追加する。
 *      この予約注文ボタンは GPO のエラー描画経路を通らないため、
 *      未選択のまま押しても「何も起きない」（内部的にはブロックされているがエラーが出ない）。
 *      → 非表示のまま生きている .gpo-clone-atc-button をこちらから click して
 *        GPO 純正のエラー表示を出させる。
 *        その間は form の submit を capture で塞ぐので、万一 GPO 側の判定を
 *        通過してもカートには入らない（予約情報の付かない追加を防ぐ）。
 *
 *   ③ GPO標準のインラインエラーが地味（12px の赤文字のみ）なので強調する。
 *
 * 設置場所:
 *   theme.liquid の {% if template.name == 'product' %} ブロック内、
 *   product-page-fix.js の後に defer で読み込む。
 */
(function () {
  'use strict';

  if (!window.location.pathname.startsWith('/products/')) return;

  // ---- 定数 ---------------------------------------------------------------
  var STYLE_ID     = 'a97-gpo-required-style';
  var SUMMARY_ID   = 'a97-gpo-missing';
  var OWN_ERR_ATTR = 'data-a97-own-error';   // 自前で足したエラー文の目印

  var CART_FORM_SEL = 'form[action*="/cart/add"]';
  var GUARD_MS = 1500;   // 自前 click 中に submit を塞いでおく時間

  // ---- 小物 ---------------------------------------------------------------
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return [].slice.call((root || document).querySelectorAll(sel)); }

  function isVisible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  // 上部固定ヘッダー＋固定検索バーの高さ（上部固定検索バー改修で定義済みのCSS変数）
  function stickyOffset() {
    var cs = window.getComputedStyle(document.documentElement);
    var h = parseInt(cs.getPropertyValue('--a97-header-h'), 10) || 0;
    var s = parseInt(cs.getPropertyValue('--a97-search-h'), 10) || 0;
    var total = h + s;
    return total > 0 ? total : 80;
  }

  function scrollToEl(el, extra) {
    if (!el) return;
    var target = Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - stickyOffset() - (extra || 16));
    var start = window.pageYOffset;

    // html に scroll-behavior:smooth が入っているので behavior 指定は保険
    try { window.scrollTo({ top: target, behavior: 'smooth' }); }
    catch (e) { window.scrollTo(0, target); }

    // 非表示タブなどではスムーススクロールのアニメーションが進まないことがある
    // （上部固定検索バーの改修で rAF が止まった件と同根）。
    // 一定時間経っても1pxも動いていなければ、smooth を切って即時スクロールに切り替える。
    // 少しでも動いていれば＝アニメーション中／完了、あるいはユーザーが自分で
    // スクロールしたということなので何もしない。
    setTimeout(function () {
      if (window.pageYOffset !== start) return;
      if (Math.abs(start - target) < 4) return;
      var de = document.documentElement;
      var prev = de.style.scrollBehavior;
      de.style.scrollBehavior = 'auto';
      window.scrollTo(0, target);
      de.style.scrollBehavior = prev;
    }, 700);
  }

  // ---- GPO の要素まわり ---------------------------------------------------
  function gpoContainer() { return $('.gpo-container'); }
  function gpoApp() { return $('.gpo-app'); }

  function labelOf(el) {
    var l = el.querySelector('.gpo-label .label-content');
    var t = l ? (l.textContent || '').replace(/\s+/g, ' ').trim() : '';
    return t || '未入力の項目';
  }

  function messageOf(el) {
    var p = el.querySelector('p.help-block.error');
    var t = p ? (p.textContent || '').replace(/\s+/g, ' ').trim() : '';
    return t || '必須項目';
  }

  // GPO が付けた .has-error のうち、実際に画面に出ているものだけ
  function errorElements() {
    return $$('.gpo-element.has-error').filter(isVisible);
  }

  // 自前の必須チェック。
  // 「ブロックされたのか、単に処理が遅いだけなのか」を見分けるために使う。
  // GPO のドロップダウンも中身は radio なので、radio/checkbox と値ありなしの2通りで足りる。
  function missingRequired() {
    return $$('.gpo-element').filter(function (el) {
      if (!isVisible(el)) return false;
      if (!el.querySelector(':scope > .gpo-label.required')) return false;

      var fields = $$('input, select, textarea', el).filter(function (i) {
        return i.type !== 'hidden' && !i.disabled;
      });
      if (!fields.length) return false;

      var choices = fields.filter(function (i) {
        return i.type === 'radio' || i.type === 'checkbox';
      });
      if (choices.length) {
        return !choices.some(function (i) { return i.checked; });
      }
      return !fields.some(function (i) { return String(i.value || '').trim() !== ''; });
    });
  }

  // ---- 未入力サマリー -----------------------------------------------------
  var ICON_WARN =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M12 9v4"/><path d="M12 17h.01"/>' +
    '<path d="M10.3 3.9 1.8 18.4A2 2 0 0 0 3.5 21.4h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>';

  function buildSummaryBox() {
    var box = document.createElement('div');
    box.id = SUMMARY_ID;
    box.className = 'a97-gpo-missing';
    box.setAttribute('role', 'alert');
    box.setAttribute('tabindex', '-1');
    box.innerHTML =
      '<div class="a97-gpo-missing__head">' + ICON_WARN +
        '<span class="a97-gpo-missing__title"></span>' +
      '</div>' +
      '<p class="a97-gpo-missing__note">下記をご選択のうえ、もう一度「カートに追加」をお試しください。</p>' +
      '<ul class="a97-gpo-missing__list"></ul>';
    return box;
  }

  function renderSummary(errs) {
    var host = gpoContainer();
    if (!host) return null;

    var box = document.getElementById(SUMMARY_ID);
    if (!box) {
      box = buildSummaryBox();
      host.insertBefore(box, host.firstChild);
    } else if (box.parentElement !== host) {
      host.insertBefore(box, host.firstChild);
    }

    box.querySelector('.a97-gpo-missing__title').textContent =
      '未選択の項目が ' + errs.length + ' 件あります';

    var list = box.querySelector('.a97-gpo-missing__list');
    list.textContent = '';

    errs.forEach(function (el) {
      var li = document.createElement('li');
      li.className = 'a97-gpo-missing__item';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a97-gpo-missing__btn';

      var name = document.createElement('span');
      name.className = 'a97-gpo-missing__label';
      name.textContent = labelOf(el);

      var msg = document.createElement('span');
      msg.className = 'a97-gpo-missing__msg';
      msg.textContent = messageOf(el);

      var arrow = document.createElement('span');
      arrow.className = 'a97-gpo-missing__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '↓';

      btn.appendChild(name);
      btn.appendChild(msg);
      btn.appendChild(arrow);
      btn.addEventListener('click', function () { jumpTo(el); });

      li.appendChild(btn);
      list.appendChild(li);
    });

    return box;
  }

  function jumpTo(el) {
    scrollToEl(el, 24);
    el.classList.add('a97-gpo-flash');
    setTimeout(function () { el.classList.remove('a97-gpo-flash'); }, 1300);

    var field = el.querySelector('input:not([type="hidden"]):not([disabled]), select, textarea');
    if (!field) return;
    // スクロール中にフォーカスで飛ばされないよう preventScroll
    setTimeout(function () {
      try { field.focus({ preventScroll: true }); } catch (e) { /* 対応外ブラウザは何もしない */ }
    }, 450);
  }

  function removeSummary() {
    var box = document.getElementById(SUMMARY_ID);
    if (box) box.remove();
  }

  // scroll=true のときだけサマリーまでスクロールする（初回表示時のみ）
  function updateSummary(scroll) {
    var errs = errorElements();
    if (!errs.length) { removeSummary(); return 0; }
    var box = renderSummary(errs);
    if (scroll && box) scrollToEl(box, 20);
    return errs.length;
  }

  // 入力が埋まると GPO 自身が .has-error を外すので、それを拾ってサマリーを更新する。
  // サマリーが出ている間だけ働く（勝手に出現させない）。
  function watchGpoErrors() {
    var app = gpoApp();
    if (!app || app._a97ReqObs) return;
    app._a97ReqObs = true;
    var timer = null;
    new MutationObserver(function () {
      if (!document.getElementById(SUMMARY_ID)) return;
      clearTimeout(timer);
      timer = setTimeout(function () { updateSummary(false); }, 80);
    }).observe(app, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  // ---- 自前のエラー描画（GPOのクローンボタンが無い場合の保険） -------------
  // 選択内容によってオプションが1つも無くなると GPO はクローンを作らないため、
  // .gpo-clone-atc-button は常に存在するとは限らない。
  function renderOwnErrors() {
    missingRequired().forEach(function (el) {
      el.classList.add('has-error');

      var msgs = el.querySelector(':scope > .messages');
      if (!msgs) {
        msgs = document.createElement('div');
        msgs.className = 'messages';
        el.appendChild(msgs);
      }
      if (!msgs.querySelector('p.help-block.error')) {
        var p = document.createElement('p');
        p.className = 'help-block error';
        p.setAttribute(OWN_ERR_ATTR, '');
        p.textContent = '必須項目';
        msgs.appendChild(p);
      }
      bindOwnClear(el);
    });
  }

  // 自前で足したエラーは自前で消す（GPO由来のものはGPOが消してくれる）
  function bindOwnClear(el) {
    if (el._a97OwnClearBound) return;
    el._a97OwnClearBound = true;
    el.addEventListener('change', function () {
      if (missingRequired().indexOf(el) >= 0) return;
      var p = el.querySelector('p.help-block.error[' + OWN_ERR_ATTR + ']');
      if (p) p.remove();
      if (!el.querySelector('p.help-block.error')) el.classList.remove('has-error');
      if (document.getElementById(SUMMARY_ID)) updateSummary(false);
    });
  }

  // ---- GPO純正のバリデーション表示を引っ張り出す --------------------------
  // 予約商品では表示ボタンが .gPreorderBtn に差し替わり、GPO のクローンボタンは
  // display:none のまま残る。クリックハンドラは生きているので、こちらから click すれば
  // GPO 純正のエラー表示（.has-error ＋ 必須項目）がそのまま出る。
  function triggerGpoValidation() {
    var clone = $('.gpo-clone-atc-button');
    var form = $(CART_FORM_SEL);
    if (!clone || !form) return false;

    // 万一 GPO 側の判定を通過しても、予約情報の付かない追加が走らないよう submit を塞ぐ
    var guard = function (ev) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
    };
    form.addEventListener('submit', guard, true);
    try { clone.click(); } catch (e) { /* noop */ }
    setTimeout(function () { form.removeEventListener('submit', guard, true); }, GUARD_MS);
    return true;
  }

  // ---- product-page-fix.js との連携 ---------------------------------------
  // 「本当にブロックされたのか」の問い合わせに答える
  document.addEventListener('a97:cart-add-probe', function (e) {
    if (!e.detail) return;
    if (errorElements().length || missingRequired().length) e.detail.blocked = true;
  });

  // ブロック確定。ここで表示を作る
  document.addEventListener('a97:cart-add-blocked', function () {
    watchGpoErrors();

    if (!errorElements().length) {
      // GPO がエラーを描画していない＝予約商品などでボタンが差し替わっているケース
      if (triggerGpoValidation()) {
        // GPO は非同期に描画するので少し待つ。出なければ自前の表示に切り替える。
        setTimeout(function () {
          if (!errorElements().length) renderOwnErrors();
          updateSummary(true);
        }, 500);
        return;
      }
      renderOwnErrors();
    }

    // GPO 自身も最初のエラー項目へスクロールするので、その後にサマリーへ寄せ直す
    setTimeout(function () { updateSummary(true); }, 350);
  });

  // ---- スタイル -----------------------------------------------------------
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = [
      /* --- 未入力サマリー --- */
      '#' + SUMMARY_ID + '{',
      '  box-sizing:border-box;width:100%;margin:0 0 14px;padding:12px 14px;',
      '  background:#fff5f5;border:1px solid #f0c0c0;border-left:4px solid #d63a3a;',
      '  border-radius:8px;animation:a97GpoAlertIn .2s ease;',
      '}',
      '@keyframes a97GpoAlertIn{from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:none;}}',
      '.a97-gpo-missing__head{display:flex;align-items:center;gap:7px;color:#c0392b;',
      '  font-size:13px;font-weight:700;line-height:1.5;}',
      '.a97-gpo-missing__head svg{width:16px;height:16px;flex:0 0 auto;}',
      '.a97-gpo-missing__note{margin:5px 0 0;font-size:11px;color:#96605c;line-height:1.6;}',
      '.a97-gpo-missing__list{list-style:none;margin:9px 0 0;padding:0;',
      '  display:flex;flex-direction:column;gap:6px;}',
      '.a97-gpo-missing__item{margin:0;}',
      '.a97-gpo-missing__btn{display:flex;align-items:center;gap:8px;width:100%;',
      '  padding:8px 10px;border:1px solid #f0c8c8;border-radius:6px;background:#fff;',
      '  color:#333;font-size:12px;line-height:1.5;text-align:left;cursor:pointer;',
      '  transition:background .15s ease,border-color .15s ease;}',
      '.a97-gpo-missing__btn:hover{background:#fff0f0;border-color:#e09a9a;}',
      '.a97-gpo-missing__btn:focus-visible{outline:2px solid #d63a3a;outline-offset:2px;}',
      '.a97-gpo-missing__label{flex:1 1 auto;min-width:0;font-weight:600;}',
      '.a97-gpo-missing__msg{flex:0 0 auto;padding:2px 6px;border-radius:3px;',
      '  background:#d63a3a;color:#fff;font-size:10px;font-weight:700;line-height:1.5;}',
      '.a97-gpo-missing__arrow{flex:0 0 auto;color:#c0392b;font-size:12px;font-weight:700;}',

      /* --- GPO標準インラインエラーの強調 --- */
      /* padding/margin は触らずレイアウトを動かさない（inset shadow で左のラインを描く） */
      '.gpo-element.has-error{background:#fff6f6;border-radius:5px;',
      '  box-shadow:inset 3px 0 0 #d63a3a;}',
      '.gpo-element.has-error > .gpo-label .label-content{color:#c0392b !important;}',
      '.gpo-element.has-error p.help-block.error{',
      '  display:inline-flex !important;align-items:center;gap:5px;',
      '  margin-top:6px !important;padding:3px 9px 3px 7px;',
      '  background:#d63a3a;color:#fff !important;',
      '  font-size:11px !important;font-weight:700;line-height:1.6;border-radius:4px;}',
      '.gpo-element.has-error p.help-block.error::before{content:"!";',
      '  display:inline-flex;align-items:center;justify-content:center;',
      '  width:13px;height:13px;border-radius:50%;background:#fff;color:#d63a3a;',
      '  font-size:10px;font-weight:700;line-height:1;}',

      /* --- ジャンプ時のフラッシュ --- */
      '.gpo-element.a97-gpo-flash{animation:a97GpoFlash 1.3s ease;}',
      '@keyframes a97GpoFlash{',
      '  0%,100%{box-shadow:inset 3px 0 0 #d63a3a;}',
      '  25%,60%{box-shadow:inset 3px 0 0 #d63a3a,0 0 0 3px rgba(214,58,58,.28);}}',

      '@media (prefers-reduced-motion: reduce){',
      '  #' + SUMMARY_ID + '{animation:none;}',
      '  .a97-gpo-missing__btn{transition:none;}',
      '  .gpo-element.a97-gpo-flash{animation:none;}}'
    ].join('\n');
    document.head.appendChild(s);
  }

  // ---- 起動 ---------------------------------------------------------------
  injectStyle();
  watchGpoErrors();

  // .gpo-app は Globo が後から挿入するので、現れたら監視を張り直す
  var boot = new MutationObserver(function () {
    watchGpoErrors();
    if (gpoApp()) boot.disconnect();
  });
  boot.observe(document.documentElement, { childList: true, subtree: true });
  setTimeout(function () { boot.disconnect(); }, 20000);
})();